import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  trialLinksActions,
  trialLinksFormSelector,
  trialLinksInfoSelector,
} from '$store/trialLinks/index';
import { t } from '$translation/helper';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { mainActions } from '$store/main';
import {
  DeleteTrialLinkAction,
  LoadInfoAction,
  TrialLinksState,
} from '$store/trialLinks/interface';
import { goBack } from '$navigation/helper';
import WebApp from '@twa-dev/sdk';

function* loadLinksWorker() {
  try {
    yield put(trialLinksActions.setIsLinksLoading(true));
    const data = yield call(Api.get, '/subscription/trial_links', {});
    yield put(trialLinksActions.setLinks(data.links));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield put(trialLinksActions.setIsLinksLoading(false));
  }
}

function* loadPersonalSubscriptionPriceWorker() {
  try {
    const data = yield call(Api.get, '/balance/subscription/personal');
    yield put(trialLinksActions.setPersonalSubscriptionPrice(data.price));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield put(trialLinksActions.setIsLoadPersonalSubscriptionPrice(false));
  }
}

function* createTrialLinkWorker() {
  const form: TrialLinksState['form'] = yield select(trialLinksFormSelector);
  yield put(toastActions.setToastLoading());
  try {
    const data = yield call(Api.post, '/subscription/trial_links/create', form);
    yield put(trialLinksActions.addLink(data.link));
    yield put(toastActions.setToastSuccess());
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deleteTrialLinkWorker(action: DeleteTrialLinkAction) {
  const id = action.payload;
  yield put(toastActions.setToastLoading());
  try {
    yield call(Api.post, `/subscription/trial_links/${id}/delete`);
    yield put(trialLinksActions.removeTrialLink(id));
    yield put(toastActions.setToastSuccess(t('toast_trial_links_link_deleted')));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadInfoWorker(action: LoadInfoAction) {
  const hash = action.payload;
  try {
    yield put(trialLinksActions.setIsLoadingInfo(true));
    const data = yield call(Api.post, '/subscription/trial_links/info', { hash });
    yield put(mainActions.setUser(data.user));
    yield put(
      trialLinksActions.setInfo({
        hash,
        userId: data.user.id,
        days: data.days,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield put(trialLinksActions.setIsLoadingInfo(false));
  }
}

/**
 * Инициирует платёж для активации trial link подписки
 * @param hash - Уникальный хеш trial link
 *
 * Процесс:
 * 1. Отправляет запрос на /pay/trial_link с хешем
 * 2. Получает payment URL от бэкенда
 * 3. Закрывает UI модалки (пользователь уходит в браузер)
 * 4. Открывает payment URL в браузере
 * 5. Бэкенд активирует подписку после успешной оплаты
 * 6. Бэкенд редиректит пользователя обратно в приложение
 */
function* initiateTrialLinkPayment(hash: string) {
  const data = yield call(Api.post, '/pay/trial_link', { hash });

  // Закрываем всё ДО редиректа на оплату
  yield put(toastActions.hideToast());
  yield put(trialLinksActions.clearInfo());

  // Открываем ссылку в браузере
  if (window.isWebApp) {
    WebApp.openLink(data.url);
  } else {
    location.href = data.url;
  }
}

function* activateWorker() {
  const info = yield select(trialLinksInfoSelector);
  if (!info?.hash) {
    return;
  }

  try {
    yield put(toastActions.setToastLoading(t('toast_trial_links_payment')));
    yield call(initiateTrialLinkPayment, info.hash);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* attachCardConfirmWorker() {
  // После привязки карты автоматически запускаем активацию
  const info = yield select(trialLinksInfoSelector);
  if (!info?.hash) {
    return;
  }

  try {
    yield put(toastActions.setToastLoading(t('toast_trial_links_payment')));
    yield call(initiateTrialLinkPayment, info.hash);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* trialLinksSaga() {
  yield all([
    takeEvery(trialLinksActions.loadLinks, loadLinksWorker),
    takeEvery(trialLinksActions.createTrialLink, createTrialLinkWorker),
    takeEvery(trialLinksActions.deleteTrialLink, deleteTrialLinkWorker),
    takeEvery(trialLinksActions.loadInfo, loadInfoWorker),
    takeEvery(trialLinksActions.activate, activateWorker),
    takeEvery(trialLinksActions.attachCardConfirm, attachCardConfirmWorker),
    takeEvery(
      trialLinksActions.loadPersonalSubscriptionPrice,
      loadPersonalSubscriptionPriceWorker,
    ),
  ]);
}
